import { MedicalResult, MedicalTranscriptEvent } from '@aws-sdk/client-transcribe-streaming';
import moment from 'moment-timezone';
import { api } from 'careand-redux/api/ApiThunks';
import { store } from 'careand-redux/store';
import { getUnwrappedDispatch } from 'careand-redux/useAppDispatch';
import { AppointmentTranscription, AppointmentTranscriptionComparison } from 'generated/api';
import {
  AppointmentTranscriptionFromAWS,
  AppointmentTranscriptionFromAWSWithVoicePartition,
  AppointmentTranscriptionWithPartialIndicators,
  TranscriptionCallback,
} from './AWSTranscribeService';
import { DeepgramTranscribeService } from './DeepgramTranscribeService';
import Bugsnag from '@bugsnag/js';

/**
 * The provider surface this orchestrator needs; satisfied by both
 * AwsTranscribeService and DeepgramTranscribeService (which emits
 * AWS-shaped MedicalTranscriptEvents so the merge pipeline is shared).
 */
export interface TranscriptionProvider {
  transcriptionCallback?: TranscriptionCallback;
  start(): Promise<void>;
  // Deepgram's stop resolves with whether the teardown actually ran (false =
  // superseded by a newer start); AWS's resolves void.
  stop(): Promise<unknown>;
  isVoicePartitioned(): boolean;
}

class TranscriptionServiceCounter {
  private _count = 0;

  public inc() {
    this._count += 1;
    this.log();
  }
  public dec() {
    this._count -= 1;
    this.log();
  }
  public log() {
    console.log('[count] TranscriptionServices: ', this._count);
  }
}

const transcriptionServiceCounter = new TranscriptionServiceCounter();

//@ts-expect-error TODO find out how to type this or scrape it altogether
window.transcriptionServiceCounter = transcriptionServiceCounter;

type TelemedicineTranscriptionCallback = (
  text: string,
  event: MedicalTranscriptEvent | undefined,
  allResults: MedicalResult[],
  merged: AppointmentTranscriptionWithPartialIndicators,
) => void;

// how many milliseconds between transcription events
// before we consider the transcription to be idle
// (ie. in silence)
const REALTIME_TRANSCRIPTION_TIMEOUT = 2000;

// How often the shadow (Deepgram) transcript is checkpointed to the server.
// Continuous upserts make durability a property of the data flow — a page
// refresh or crash loses at most this window, mirroring how the primary
// transcript survives via the component-level auto-backup. Each checkpoint
// uploads the full document (byte-identical bodies are skipped), so the
// interval trades refresh-loss window against upload/write amplification
// over a long visit.
const SHADOW_BACKUP_INTERVAL = 15_000;

export const TRANSCRIPTION_STATUS = {
  active: 'active',
  transcribing: 'transcribing',
  inactive: 'inactive',
  starting: 'starting',
  ending: 'ending',
  error: 'error',
} as const;

export type TRANSCRIPTION_STATUS = (typeof TRANSCRIPTION_STATUS)[keyof typeof TRANSCRIPTION_STATUS];

/**
 * Responsible for:
 * - starting a mic + portal transcription
 * - automatically backing up the transcription
 * - sending events (through callbacks)
 *    - on new transcription event
 *    - on realtimeTranscribingStatusChanged (whether has received event in last 2 seconds)
 */
export class TelemedicineTranscriptionService {
  private isTranscribing = false;
  private results: MedicalResult[] = [];
  private oldTranscription: AppointmentTranscription | undefined;
  // Current shadow session's results. Save operations capture the array
  // reference at schedule time, so a quick restart replacing this field can
  // never make an older session's save read a newer session's data.
  private shadowSessionResults: MedicalResult[] = [];
  // The shadow twin of oldTranscription: previously saved shadow messages
  // (earlier sessions / pre-refresh segments) that new checkpoints prepend.
  // Only queue operations mutate it, so every save sees the history that
  // precedes it in commit order.
  private oldShadowTranscription: AppointmentTranscriptionComparison | undefined;
  private shadowHistoryLoaded = false;
  // Serializes every shadow persistence step (history load, checkpoints,
  // final saves): a slow checkpoint can never commit after — and revert — a
  // newer one, and a new session's history load always runs after the
  // previous session's final save.
  private shadowPersistQueue: Promise<void> = Promise.resolve();
  private shadowBackupTimer: NodeJS.Timeout | null = null;
  private lastSavedShadowPayload?: string;
  private shadowSaveErrorReported = false;
  private realTimeTranscribingTimer: NodeJS.Timeout | null = null;
  private lastTranscriptionEventDate?: number;

  constructor(
    private awsService: TranscriptionProvider,
    private appointmentId: string,
    private transcriptionCallback: TelemedicineTranscriptionCallback | undefined,
    private onTranscriptionStatusChanged?: (value: TRANSCRIPTION_STATUS) => void,
    private shadow?: DeepgramTranscribeService,
  ) {}

  async loadExistingTranscription() {
    try {
      this.oldTranscription = await this.getDispatch()(
        api.Appointments.getAppointmentTranscription({ id: this.appointmentId }),
      );
      // If there's existing transcription, trigger callback to show it immediately
      if (this.oldTranscription?.messages?.length && this.transcriptionCallback) {
        this.transcriptionCallback(
          '', // no new text
          undefined, // no new event
          [], // no new results
          this.getMergedTranscription(true, true, false), // show existing with partial indicators
        );
      }
    } catch (error: any) {
      console.error('Failed to load existing transcription:', error);
      Bugsnag.notify(error);
    }
  }

  private onTranscriptionReceived() {
    if (!this.onTranscriptionStatusChanged) {
      return;
    }
    this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.transcribing);
    if (this.realTimeTranscribingTimer !== null) {
      clearTimeout(this.realTimeTranscribingTimer);
    }
    this.realTimeTranscribingTimer = setTimeout(() => {
      if (!this.isTranscribing) {
        this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.inactive);
      }
      this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.active);
    }, REALTIME_TRANSCRIPTION_TIMEOUT);
  }

  async start() {
    if (this.isTranscribing) {
      throw { message: 'Already Transcribing' };
    }
    transcriptionServiceCounter.inc();
    this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.starting);

    this.awsService.transcriptionCallback = (text, event, ...args) => {
      const results = event?.Transcript?.Results;
      if (!results || results.length === 0) {
        return;
      }
      this.results = [...this.results, ...results];
      this.lastTranscriptionEventDate = moment().unix();
      const isVoicePartitioned = this.awsService.isVoicePartitioned();
      this.transcriptionCallback?.(
        text,
        event,
        this.results,
        //@ts-expect-error
        this.getMergedTranscription(true, !isVoicePartitioned, isVoicePartitioned),
      );
      this.onTranscriptionReceived();
    };

    await this.loadExistingTranscription();

    this.startShadow();

    try {
      await this.awsService.start();
    } catch (err: any) {
      // Surface the failure instead of leaving the UI stuck on 'starting':
      // stop the shadow (releasing any teed audio) and report error status.
      transcriptionServiceCounter.dec();
      this.stopShadowAndBackup();
      this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.error);
      throw err;
    }

    this.isTranscribing = true;
    this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.transcribing);
  }

  /**
   * The shadow provider transcribes the same audio without touching the UI.
   * Its transcript is continuously checkpointed to the server as the exact
   * twin of the clinical AppointmentTranscription (same id, same shape,
   * separate table), so the two can be diffed apples-to-apples and a page
   * refresh loses at most one checkpoint window on either side.
   * Shadow failures are logged and swallowed — they must never affect the
   * primary transcription.
   */
  private startShadow() {
    if (!this.shadow) {
      return;
    }
    const sessionResults: MedicalResult[] = [];
    this.shadowSessionResults = sessionResults;
    this.shadowSaveErrorReported = false;
    // Through the queue: runs after any pending final save from the previous
    // session, so it always reads the fully committed history.
    this.enqueueShadowPersist(() => this.loadShadowHistory());
    this.shadow.transcriptionCallback = (_text, event) => {
      const results = event?.Transcript?.Results;
      if (results?.length) {
        sessionResults.push(...results);
        this.scheduleShadowBackup();
      }
    };
    this.shadow.start().catch((err: any) => {
      console.error('[Deepgram shadow] failed to start:', err);
    });
  }

  private enqueueShadowPersist(op: () => Promise<void>) {
    this.shadowPersistQueue = this.shadowPersistQueue.then(op).catch((err: any) => {
      console.error('[Deepgram shadow] persistence step failed:', err);
    });
  }

  private async loadShadowHistory() {
    const response = await this.getDispatch()(
      api.Appointments.getAppointmentTranscriptionComparison({ id: this.appointmentId }),
      { meta: true },
    );
    if (response.isSuccess) {
      this.oldShadowTranscription = response.result as AppointmentTranscriptionComparison;
      this.shadowHistoryLoaded = true;
    } else if (response.status === 404) {
      // Genuinely no shadow transcript saved for this appointment yet
      this.oldShadowTranscription = undefined;
      this.shadowHistoryLoaded = true;
    } else {
      // Fail closed: with unknown history a checkpoint would overwrite every
      // previously saved segment. Checkpoints retry this load until it works.
      this.shadowHistoryLoaded = false;
      console.error(
        '[Deepgram shadow] failed to load existing twin (status',
        response.status,
        ') — checkpointing paused until it loads',
      );
    }
  }

  private scheduleShadowBackup() {
    if (this.shadowBackupTimer) {
      return;
    }
    this.shadowBackupTimer = setTimeout(() => {
      this.shadowBackupTimer = null;
      const sessionResults = this.shadowSessionResults;
      this.enqueueShadowPersist(() => this.saveShadowTranscription(sessionResults));
    }, SHADOW_BACKUP_INTERVAL);
  }

  /** Runs only inside the persistence queue. */
  private async saveShadowTranscription(sessionResults: MedicalResult[]) {
    if (!this.shadow) {
      return;
    }
    if (!this.shadowHistoryLoaded) {
      await this.loadShadowHistory();
      if (!this.shadowHistoryLoaded) {
        // Still can't read the history — skip rather than clobber; the next
        // checkpoint retries.
        return;
      }
    }

    const built = this.shadow.isVoicePartitioned()
      ? AppointmentTranscriptionFromAWSWithVoicePartition(this.appointmentId, sessionResults)
      : AppointmentTranscriptionFromAWS(this.appointmentId, sessionResults);

    const messages = [...(this.oldShadowTranscription?.messages ?? []), ...built.messages];
    if (!messages.length) {
      return;
    }

    // Interim-only windows and silences rebuild the same document — skip
    // byte-identical bodies instead of re-uploading the full transcript.
    const payload = JSON.stringify(messages);
    if (payload === this.lastSavedShadowPayload) {
      return;
    }

    const response = await this.getDispatch()(
      api.Appointments.editOrAddAppointmentTranscriptionComparison({
        id: this.appointmentId,
        appointmentTranscriptionComparison: { id: this.appointmentId, messages },
      }),
      { meta: true },
    );
    if (!response.isSuccess) {
      // lastSavedShadowPayload stays unchanged so the next checkpoint retries
      console.error('[Deepgram shadow] checkpoint save failed (status', response.status, ')');
      if (!this.shadowSaveErrorReported) {
        this.shadowSaveErrorReported = true;
        Bugsnag.notify(
          new Error(`Shadow transcription checkpoint failed (status ${response.status})`),
        );
      }
      return;
    }
    this.lastSavedShadowPayload = payload;
  }

  private stopShadowAndBackup() {
    if (!this.shadow) {
      return;
    }
    if (this.shadowBackupTimer) {
      clearTimeout(this.shadowBackupTimer);
      this.shadowBackupTimer = null;
    }
    const shadow = this.shadow;
    // Captured now: a quick restart replaces the field, but this session's
    // final save must persist THIS session's results.
    const sessionResults = this.shadowSessionResults;
    this.enqueueShadowPersist(async () => {
      try {
        // Stop flushes Deepgram's final results into this session's collector
        await shadow.stop();
      } catch (err: any) {
        console.error('[Deepgram shadow] failed to stop:', err);
      }
      await this.saveShadowTranscription(sessionResults);
    });
  }

  private getMergedTranscription(
    withPartials: boolean,
    withPartialIndicators: true,
    isVoicePartitioned: boolean,
  ): AppointmentTranscriptionWithPartialIndicators;
  private getMergedTranscription(
    withPartials: boolean,
    withPartialIndicators: false,
    isVoicePartitioned: boolean,
  ): AppointmentTranscription;
  private getMergedTranscription(
    withPartials: boolean,
    withPartialIndicators: boolean,
    isVoicePartitioned: boolean,
  ) {
    let transcription;
    if (isVoicePartitioned) {
      transcription = AppointmentTranscriptionFromAWSWithVoicePartition(
        this.appointmentId,
        this.results,
      );
    } else {
      transcription = AppointmentTranscriptionFromAWS(
        this.appointmentId,
        this.results,
        withPartials,
        //@ts-expect-error
        withPartialIndicators,
      );
    }
    if (this.oldTranscription) {
      transcription.messages = [...this.oldTranscription.messages, ...transcription.messages];
    }
    return transcription;
  }

  async stop() {
    console.log('[debug] stopping...');
    if (!this.isTranscribing) {
      console.log('[debug] had stoppped already');
      return;
    }
    this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.ending);
    console.log('[debug] stopping... for realsies this time');
    transcriptionServiceCounter.dec();
    this.isTranscribing = false;
    if (this.realTimeTranscribingTimer) {
      console.log('[debug] stopping... clearing transcribing timer');
      clearTimeout(this.realTimeTranscribingTimer);
      this.realTimeTranscribingTimer = null;
    }
    try {
      console.log('[debug] stopping... awsService.stop');
      await this.awsService?.stop();
    } catch (err: any) {
      console.log(err);
      Bugsnag.notify(err);
    }
    // Fire-and-forget: the shadow teardown must not delay the stop flow
    this.stopShadowAndBackup();
    console.log('[debug] stopping... dispatching state change to inactive');
    this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.inactive);
  }

  private getDispatch() {
    return getUnwrappedDispatch(store.dispatch);
  }
}
